import { Link } from '@tanstack/react-router';
import type { AdminCityDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { COUNTRIES } from '@upme/ui/data/countries';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../../shared/components/empty-state';

interface Props {
  cities: ReadonlyArray<AdminCityDto>;
}

export function CitiesTable({ cities }: Props) {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return cities.filter((c) => {
      if (!showInactive && !c.isActive) return false;
      if (!term) return true;
      return c.name.toLowerCase().includes(term) || c.slug.toLowerCase().includes(term);
    });
  }, [cities, search, showInactive]);

  if (cities.length === 0) {
    return (
      <EmptyState
        title="Городов пока нет"
        description="Создайте первый город, чтобы заполнить контент-каталог."
        cta={
          <Button asChild>
            <Link to="/cities/new">Создать первый город</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Поиск по названию или slug"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex items-center gap-2">
          <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
          <Label htmlFor="show-inactive">Показать неактивные</Label>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Страна</TableHead>
            <TableHead>Часовой пояс</TableHead>
            <TableHead>Чат</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((c) => {
            const country = COUNTRIES.find((x) => x.code === c.countryCode);
            return (
              <TableRow key={c.id} className="cursor-pointer">
                <TableCell>
                  <Link to="/cities/$cityId" params={{ cityId: c.id }} className="hover:underline">
                    {c.name}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs">{c.slug}</TableCell>
                <TableCell>{country ? `${country.flag} ${c.countryCode}` : c.countryCode}</TableCell>
                <TableCell>{c.timezone}</TableCell>
                <TableCell>
                  <ChatBadge city={c} />
                </TableCell>
                <TableCell>
                  {c.isActive ? (
                    <Badge variant="default">active</Badge>
                  ) : (
                    <Badge variant="secondary">inactive</Badge>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Бейдж трансляции в городской чат (#146). title = null бывает только у города без чата. */
function ChatBadge({ city }: { city: AdminCityDto }) {
  if (city.telegramChatTitle === null) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant={city.isBroadcastActive ? 'default' : 'secondary'} title={city.telegramChatTitle}>
      {city.isBroadcastActive ? 'вкл' : 'выкл'}
    </Badge>
  );
}
